/**
 * What depends on having an account and is not a screen: the guard of the routes in §4, signing
 * out, and the session that ends because the account was blocked or deleted (§5.9).
 */
import { type AkpError } from '@akpl/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';

import { getApiErrorHandler, setApiErrorHandler } from '../../api';
import { useAuth } from '../../auth';
import { setWebApiErrorHandler } from '../api';
import { useResumeIntent } from '../resume';
import { useWebSession } from '../session/WebSessionProvider';
import { saveIntent, takeIntent, useWebUi } from '../store';

const ACCOUNT_PATHS = new Set([
  '/mi-musica',
  '/mi-musica/recientes',
  '/cuenta',
  '/cantar',
  '/fiesta',
]);

export function isAccountPath(pathname: string): boolean {
  return ACCOUNT_PATHS.has(pathname.replace(/\/$/, '') || '/');
}

/**
 * The account routes of §4. A visitor, or an anonymous guest of `/guest`, gets E-06 over the
 * layout and keeps the address: after signing up they land where they were going, not on a
 * redirect. The page below is not mounted, so nothing of it asks the API for data.
 */
export function AccountGuard({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { href } = useLocation();
  const openOverlay = useWebUi((s) => s.openOverlay);
  const blocked = auth.status !== 'loading' && !auth.isAccount;

  useEffect(() => {
    if (!blocked) return;
    saveIntent({ type: 'path', to: href });
    openOverlay({ kind: 'requireAccount' });
  }, [blocked, href, openOverlay]);

  if (!auth.isAccount) return <div data-testid="account-guard" className="flex-1 bg-bg" />;
  return children;
}

/**
 * §5.9. The account pages leave first: with the session gone, their guard would ask a person who
 * just signed out to create an account.
 */
export function useSignOut(): () => Promise<void> {
  const auth = useAuth();
  const session = useWebSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const setPaywallAfterAuth = useWebUi((s) => s.setPaywallAfterAuth);

  return useCallback(async () => {
    if (isAccountPath(pathname)) await navigate({ to: '/' });
    takeIntent();
    setPaywallAfterAuth(false);
    try {
      await session.shutdown();
    } catch {
      /* A screen that fails to close must not keep the person signed in. */
    }
    await auth.signOut();
    queryClient.clear();
  }, [auth, navigate, pathname, queryClient, session, setPaywallAfterAuth]);
}

/** §5.9, for both API clients: any answer can be the first to say the account is gone. */
function useAccountClosedHandler(): void {
  const auth = useAuth();
  const signOut = useSignOut();
  const openOverlay = useWebUi((s) => s.openOverlay);

  const latest = useRef({ auth, signOut, openOverlay });
  useEffect(() => {
    latest.current = { auth, signOut, openOverlay };
  });

  useEffect(() => {
    let closing = false;
    const handle = (error: AkpError) => {
      if (error.code !== 'account_blocked' && error.code !== 'account_deleted') return;
      const current = latest.current;
      // `closing`: signing out calls the API, which answers the same. The user check covers the
      // requests that were still in flight when the session ended.
      if (closing || !current.auth.client?.currentUser()) return;
      closing = true;
      const reason = error.code === 'account_blocked' ? 'blocked' : 'deleted';
      void current
        .signOut()
        .catch(() => undefined)
        .finally(() => {
          closing = false;
          current.openOverlay({ kind: 'accountClosed', reason });
        });
    };
    // One slot, shared with `installApiSessionGuard` (`/app` and `/guest` rely on it): chained, and
    // handed back when the person leaves the web routes.
    const previous = getApiErrorHandler();
    setApiErrorHandler((error, info) => {
      previous?.(error, info);
      handle(error);
    });
    setWebApiErrorHandler(handle);
    return () => {
      setApiErrorHandler(previous);
      setWebApiErrorHandler(null);
    };
  }, []);
}

/** Mounted once in `WebLayout`, inside the session provider. */
export function AccountLifecycle() {
  useResumeIntent();
  useAccountClosedHandler();
  return null;
}
